import { useState } from "react";
import { Document, Page } from "react-pdf";
import { Pagination } from "@jhool-io/fe-components";
import ListState from "../../../../components/ListState/ListState";
import { useFetchDocument } from "../../../../hooks/queries";
import Loader from "../../../../shared-ui/Loader/Loader";

interface ViewInvoiceNoteDocProps {
    url: string;
    contentType: string;
    title?: string;
    numOfPages?: number;
}

export default function ViewInvoiceNoteDoc({
    url,
    contentType,
    title = "Invoice note attachment",
    numOfPages,
}: ViewInvoiceNoteDocProps) {
    const [numPages, setNumPages] = useState<null | number>(numOfPages || null);
    const [pageNumber, setPageNumber] = useState(1);

    const { data, isLoading, error } = useFetchDocument(url, Boolean(url));

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return (
            <ListState
                isError
                errorMsg="Could not load document at this time"
                stateHelperText="Try reloading the page to fix this error"
            />
        );
    }

    if (data && /^image/.test(contentType)) {
        return (
            <div className="h-full w-full">
                <img
                    className="h-full w-full max-w-full object-contain"
                    src={`data:image/png;base64,${data.data}`}
                    alt={title}
                />
            </div>
        );
    }

    return (
        <div>
            {numPages && (
                <div
                    role="banner"
                    className="w-full h-[120px] bg-secondary [border:1px_solid_var(--color-primary)] rounded-md mt-4 flex items-center justify-center pl-4 flex-col"
                >
                    <p className="font-medium text-base mb-[--spacing-items] capitalize">
                        {title}
                    </p>
                    <p className="text-textSec font-medium text-caption">
                        {pageNumber}/{numPages || "--"} pages
                    </p>
                </div>
            )}
            <div className="flex items-center justify-center bg-secondary rounded-md text-base">
                <Document
                    error={
                        <span className="text-base font-medium text-danger h-[100px] flex items-center justify-center">
                            Could not load pdf at this time
                        </span>
                    }
                    loading={
                        <span className="text-base flex items-center justify-center h-[100px]">
                            Loading pdf..
                        </span>
                    }
                    onLoadSuccess={({ numPages: nextNumPages }) => {
                        setNumPages(nextNumPages);
                        setPageNumber(1);
                    }}
                    onItemClick={({ pageNumber: itemPageNumber }) => {
                        setPageNumber(Number(itemPageNumber));
                    }}
                    file={`data:image/png;base64,${data?.data}`}
                >
                    <Page
                        pageNumber={pageNumber}
                        height={550}
                        scale={1.2}
                        renderAnnotationLayer={false}
                    />
                </Document>
            </div>
            {numPages && numPages > 1 && (
                <Pagination
                    totalCount={numPages}
                    totalPages={numPages}
                    currentPage={pageNumber}
                    onPageChange={(page) => setPageNumber(page)}
                    pageSize={1}
                    isCentered
                />
            )}
        </div>
    );
}
